import React from 'react';
import moment from 'moment'
import momentTimezone from 'moment-timezone'
import './graphmodal.css';
import constants from '../../config/constants'
import toast from '../../components/toast'
import Graph from './graph';
import sensorService from '../../services/sensorService';
import DatePicker from '../../components/datepicker'
import LoadingWheel from '../../components/loadingwheel'
import settingsService from '../../services/settingsService';
import pinConfigurationService from '../../services/pinConfigurationService';
import temperatureService from '../../services/temperatureService';
import Select from '../select';
import { Button, Checkbox, Tooltip } from 'antd';
import {
  CloseOutlined
} from '@ant-design/icons';
import Plotly from 'plotly.js';
import {
  formatLegendLabel,
  pinConfigMapByRepresentation,
  resolvePlotUnit,
} from '../../helpers/graphSensorUnit';
import { clampEndDateForStart, clampStartDateForEnd, isGraphDateRangeValid } from '../../helpers/graphDateRange';


export interface GraphProps {
  closeGraphModal: () => void;
  SensorBoxName: string;
  sensorBox: any;
}

export interface GraphState {
  x_time: any;
  y_data: any;
  sensorData: any;
  startDate: Date;
  endDate: Date;
  isLoading: boolean;
  selectedSensor: any;
  dateError: string;
  configOptions: any,
  selectedConfig: any,
  sensorOptions: any,
  configMap: any,
  configDateString: String,
  selectedParameters: any,
  selectedGrid: any,
  fieldTestOfflineDuration: any;
  powerConsumption: any;
  weatherObservations: any[];
  threshold?: any;
  isFilterredData: boolean;
  sensorGroups: any;
  selectedSensorsByType: any;
  sensorboxTimezone: string | null;
  useLocationTimezone: boolean;
  temperatureUnit: 'F' | 'C';
  pinConfigByRepresentation: Map<string, any>;
}

const MAX_SENSORS_PER_TYPE = 6;
const SPECIAL_GRAPH_FIELDS = new Set([
  'powerConsumption',
  'outsideWeatherTemperature',
  'outsideWeatherHumidity',
]);

class GraphModal extends React.PureComponent<GraphProps, GraphState>{
  private hvacRequestId = 0
  private powerLoaded = false
  private weatherLoaded = false
  private powerInflight: Promise<any> | null = null
  private weatherInflight: Promise<any> | null = null

  constructor(props: GraphProps) {
    super(props);
    this.state = {
      x_time: {},
      y_data: {},
      sensorData: [],
      startDate: this.getDateWeekAgo(),
      endDate: new Date(),
      isLoading: false,
      sensorOptions: [],
      selectedSensor: { _id: '', name: "Select Sensor" },
      dateError: '',
      configOptions: [],
      selectedConfig: { _id: '', name: "Select Version" },
      configMap: new Map(),
      configDateString: "",
      selectedParameters: [],
      selectedGrid: [{}],
      fieldTestOfflineDuration: null,
      powerConsumption:[],
      weatherObservations: [],
      isFilterredData: true,
      sensorGroups: {},
      selectedSensorsByType: {},
      sensorboxTimezone: this.props.sensorBox?.timezone || null,
      useLocationTimezone: false,
      temperatureUnit: 'F' as 'F' | 'C',
      pinConfigByRepresentation: new Map(),
    }
  }

  handleFilterDataToggle = async() => {
    this.setState({ isFilterredData: !this.state.isFilterredData }, async () => {
        await this.setGraphData()
    })
  };

  getDateWeekAgo() {
    const date = new Date()
    const pastDate = new Date().getDate() - 7;
    date.setDate(pastDate);
    return date
  }

  validateDate() {
    return isGraphDateRangeValid(this.state.startDate, this.state.endDate)
  }

  async componentDidMount() {
    if (this.props.sensorBox?.asset_type) {
      const unit = temperatureService.getTemperatureUnit(this.props.sensorBox.asset_type);
      this.setState({ temperatureUnit: unit });
    }
    try {
      const pinConfigResp = await pinConfigurationService.getPinConfiguration();
      const pinConfigByRepresentation = pinConfigMapByRepresentation(
        pinConfigResp?.data?.pin_configuration || []
      );
      await new Promise<void>((resolve) => {
        this.setState({ pinConfigByRepresentation }, () => resolve());
      });
    } catch {
      // graph can still render without pin-config units
    }
    await this.getConfigHistory()
    await this.loadHvacData()
  }

  async getConfigHistory() {
    const configHistory = await sensorService.getSensorBoxConfigHistory(this.props.SensorBoxName)
    if (this.props.sensorBox?.sensor_box_source === 'external') {
      const pinConfigResp = await pinConfigurationService.getPinConfiguration();
      const { pin_configuration } = pinConfigResp.data;
      configHistory.forEach((config: any) => {
        config.sensors.forEach((sensor: any) => {
          pin_configuration.forEach((pinConfig: any) => {
            if (pinConfig.representation === sensor.representation) {
              sensor.display_name = pinConfig.display_name
              }
          })
        })
      })
    }

    const filteredConfigHistory = this.filterConfigHistoryByCommissionedDate(configHistory, this.props.sensorBox?.commissioned_date);
    await this.setConfigOptions(filteredConfigHistory);
    await this.setSensorOptions(filteredConfigHistory[filteredConfigHistory.length - 1])    //to set latest sensor configuration
  }

  filterConfigHistoryByCommissionedDate = (
    configHistory: any[] = [],
    commissionedDate?: number
  ) => {
    if (!commissionedDate) {
      return configHistory;
    }
  
    const filtered = configHistory.filter((config: any) => {
      if (!config?.start_date) {
        return true;
      }
      return Number(config.start_date) >= Number(commissionedDate);
    });
  
    // If found matching configs, return them
    if (filtered.length > 0) {
      return filtered;
    }

    // Fallback: highest version (pre-commission testing configs)
    let latestConfig = configHistory[0];
    for (const config of configHistory) {
      if ((config?.version ?? 0) > (latestConfig?.version ?? 0)) {
        latestConfig = config;
      }
    }
    return latestConfig ? [latestConfig] : [];
  };

  async setConfigOptions(data: any) {
    let configOptions: any = []
    let configMap: any = new Map()
    data.forEach((config: any) => {
      const startDate = config.start_date
        ? moment(new Date(config.start_date)).format("MM-DD-YYYY")
        : '';
      const endDate = config.end_date
        ? moment(new Date(config.end_date)).format("MM-DD-YYYY")
        : 'Now';
      const versionDateRange = startDate ? ` (${startDate} - ${endDate})` : '';
      configOptions.push({
        _id: (config.version).toString(),
        name: `v${(config.version).toString()}${versionDateRange}`
      })
      configMap.set(config.version, config)
    })
    this.setState({
      configOptions,
      configMap,
      selectedConfig: configOptions[configOptions.length - 1] || { _id: '', name: "Select Version" }
    })
    this.setConfigDateString(data[data.length - 1])
  }

  async setSensorOptions(configData: any) {
    let sensorOptions: any = []
    let sensorGroups: any = {}
    
    configData?.sensors?.forEach((sensor: any) => {
        const pinType = sensor.type || 'default';
        if (!sensorGroups[pinType]) {
          sensorGroups[pinType] = {
            type: pinType,
            sensors: [],
            displayName: this.getPinTypeDisplayName(pinType)
          };
        }

        const live = this.props.sensorBox?.sensors?.find(
          (s: any) => s.representation === sensor.representation && s.sensor_name === sensor.sensor_name
        );
        const plotUnit = resolvePlotUnit(
          {
            ...sensor,
            pin_type: live?.pin_type,
            unit: live?.unit,
            type: pinType,
          },
          this.state.pinConfigByRepresentation,
          this.state.temperatureUnit
        );
        const baseLabel = `${sensor.display_name}${configData?.sensors.filter((item: any) => item.representation === sensor.representation).length > 1 ? ` - ${sensor?.sensor_name}` : ''}`;

        const sensorOption = { 
          _id: `${sensor.representation}_${sensor.sensor_name}`, 
          name: formatLegendLabel(baseLabel, plotUnit),
          representation: sensor.representation,
          pin_type: pinType,
          sensor_type: sensor.sensor_type
        };
        
        sensorOptions.push(sensorOption);
        sensorGroups[pinType].sensors.push(sensorOption);
    });
    
    // Add special sensors
    const hasCurrent = this.hasCurrentPinType();
    const { pinConfigByRepresentation, temperatureUnit } = this.state;
    const specialSensors = [
      ...(hasCurrent ? [{
        _id:'powerConsumption',
        name: formatLegendLabel('Power Consumption', resolvePlotUnit(
          { representation: 'powerConsumption', type: 'power' },
          pinConfigByRepresentation,
          temperatureUnit
        )),
        representation:'powerConsumption',
        pin_type:'power'
      }] : []),
      {
        _id:'outsideWeatherTemperature',
        name: formatLegendLabel('Outside Weather Temperature', resolvePlotUnit(
          { representation: 'outsideWeatherTemperature', type: 'temperature' },
          pinConfigByRepresentation,
          temperatureUnit
        )),
        representation:'outsideWeatherTemperature',
        pin_type:'temperature'
      },
      {
        _id:'outsideWeatherHumidity',
        name: formatLegendLabel('Outside Weather Humidity', resolvePlotUnit(
          { representation: 'outsideWeatherHumidity', type: 'humidity' },
          pinConfigByRepresentation,
          temperatureUnit
        )),
        representation:'outsideWeatherHumidity',
        pin_type:'humidity'
      }
    ];
    
    specialSensors.forEach(sensor => {
      sensorOptions.push(sensor);
      if (!sensorGroups[sensor.pin_type]) {
        sensorGroups[sensor.pin_type] = {
          type: sensor.pin_type,
          sensors: [],
          displayName: this.getPinTypeDisplayName(sensor.pin_type)
        };
      }
      sensorGroups[sensor.pin_type].sensors.push(sensor);
    });
    
    this.setState({ sensorOptions, sensorGroups })
  }
  
  // Check if sensorbox has any current pin type
  hasCurrentPinType = () => {
    return this.props.sensorBox?.sensors?.some((sensor: any) => sensor?.pin_type === "current");
  };

  getPinTypeDisplayName = (pinType: string) => {
    const displayNames: any = {
      'temperature': 'Temperature Sensors',
      'humidity': 'Humidity Sensors',
      'pressure': 'Pressure Sensors',
      'power': 'Power Sensors',
      'weather': 'Weather Sensors',
      'default': 'Other Sensors'
    };
    return displayNames[pinType] || pinType;
  }

  handleConfigSelection = async (e: any) => {
    if (this.state.selectedConfig !== e) {
      const selectedConfigDetails = this.state.configMap.get(parseInt(e._id))
      this.setConfigDateString(selectedConfigDetails)
      this.setState({ selectedConfig: e, selectedParameters: [], selectedGrid: [{}], sensorData: [], y_data: {}, x_time: {}, powerConsumption: [], weatherObservations: [] }, async () => {
        await this.setSensorOptions(selectedConfigDetails)
        await this.loadHvacData()
      })
    }

  }

  setConfigDateString = (selectedConfigDetails: any) => {
    let configDateString = ''
    if (selectedConfigDetails?.start_date && selectedConfigDetails?.end_date) {
      configDateString = `( Configuration v${selectedConfigDetails.version} active b/w ${moment(new Date(selectedConfigDetails?.start_date)).format("MM/DD/YYYY")} and ${moment(new Date(selectedConfigDetails?.end_date)).format("MM/DD/YYYY")} )`
    } else if (selectedConfigDetails?.start_date) {
      configDateString = ` ( Configuration v${selectedConfigDetails.version} active from ${moment(new Date(selectedConfigDetails?.start_date)).format("MM/DD/YYYY")} )`
    }
    this.setState({ configDateString })

  }

  getSelectedIds = () => {
    const selectedIds = this.state.selectedGrid?.flatMap((grid: any) => grid?.parameters || []) || []
    return selectedIds.filter((id: string) => !!id)
  }

  async getDatainChunks(fields?: string[]) {
    if (!this.state.selectedConfig?._id) {
      return []
    }
    if (fields && !fields.length) {
      return []
    }
    let graphData: Array<any> = []
    let chunk: Array<any> = []
    let chunkLastTime = null
    while (true) {
      chunk = await sensorService.getGraphData(
        this.props.SensorBoxName,
        this.state.startDate,
        this.state.endDate,
        chunkLastTime,
        this.state.selectedConfig._id,
        fields
      );
      if (chunk.length === 0) {
        break;
      }
      chunkLastTime = chunk[chunk.length - 1].added_time
      graphData = [...graphData, ...chunk]
    }

    return graphData
  }
  beginPowerRequest() {
    if (!this.powerInflight) {
      const energySystemId = this.props.sensorBox?.sensorbox_group || this.props.SensorBoxName
      const energySystemType = this.props.sensorBox?.sensorbox_group ? 'group' : 'single'
      const request = sensorService.getEnergyConsumptionData(
        energySystemId,
        energySystemType,
        this.state.startDate,
        this.state.endDate
      )
      this.powerInflight = request
      request.finally(() => {
        if (this.powerInflight === request) this.powerInflight = null
      })
    }
    return this.powerInflight
  }

  beginWeatherRequest() {
    if (!this.weatherInflight) {
      const request = sensorService.getWeatherObservationsForSensorbox(
        this.props.SensorBoxName,
        this.state.startDate,
        this.state.endDate
      ).catch(() => [])
      this.weatherInflight = request
      request.finally(() => {
        if (this.weatherInflight === request) this.weatherInflight = null
      })
    }
    return this.weatherInflight
  }

  resetSpecialSensorCache() {
    this.powerLoaded = false
    this.weatherLoaded = false
    this.powerInflight = null
    this.weatherInflight = null
  }

  async loadHvacData() {
    const requestId = ++this.hvacRequestId
    this.resetSpecialSensorCache()

    if (!this.validateDate()) {
      this.setState({ dateError: constants.message.dateError, isLoading: false })
      return
    }
    if (!this.state.selectedConfig?._id) {
      return
    }

    this.setState({ isLoading: true, dateError: '', powerConsumption: [], weatherObservations: [] })
    try {
      const selectedIds = this.getSelectedIds()
      if (selectedIds.includes('powerConsumption')) this.beginPowerRequest()
      if (selectedIds.includes('outsideWeatherTemperature') || selectedIds.includes('outsideWeatherHumidity')) {
        this.beginWeatherRequest()
      }
      const [settings, data] = await Promise.all([
        this.state.fieldTestOfflineDuration != null
          ? Promise.resolve(null)
          : settingsService.getSettings(),
        this.getDatainChunks(),
      ])
      if (requestId !== this.hvacRequestId) return
      const nextState: Partial<GraphState> = { sensorData: data, isLoading: false }
      if (settings) {
        nextState.fieldTestOfflineDuration = settings?.data?.cycle_offline_duration
      }
      await new Promise<void>((resolve) => {
        this.setState(nextState as GraphState, () => resolve())
      })
      if (requestId !== this.hvacRequestId) return
      await this.syncPlotFromSelection(requestId)
    } catch (error) {
      if (requestId !== this.hvacRequestId) return
      this.setState({ isLoading: false })
      toast.error(error as any)
    }
  }

  async syncPlotFromSelection(requestId = this.hvacRequestId) {
    const selectedIds = this.getSelectedIds()
    const wantsPower = selectedIds.includes('powerConsumption')
    const wantsWeather = selectedIds.includes('outsideWeatherTemperature') || selectedIds.includes('outsideWeatherHumidity')

    if (!selectedIds.length) {
      this.setState({ y_data: {}, x_time: {}, isLoading: false })
      return
    }

    const needPower = wantsPower && !this.powerLoaded
    const needWeather = wantsWeather && !this.weatherLoaded
    if (needPower || needWeather) {
      this.setState({ isLoading: true })
    }

    let powerConsumption = this.state.powerConsumption
    let weatherObservations = this.state.weatherObservations
    try {
      if (needPower) {
        const energyResp = await this.beginPowerRequest()
        if (requestId !== this.hvacRequestId) return
        powerConsumption = Array.isArray(energyResp) ? energyResp : (energyResp?.data ?? [])
        this.powerLoaded = true
      }
      if (needWeather) {
        const weather = await this.beginWeatherRequest()
        if (requestId !== this.hvacRequestId) return
        weatherObservations = Array.isArray(weather) ? weather : (weather?.data ?? [])
        this.weatherLoaded = true
      }
    } catch (error) {
      if (requestId !== this.hvacRequestId) return
      this.setState({ isLoading: false })
      toast.error(error as any)
      return
    }

    if (requestId !== this.hvacRequestId) return
    await new Promise<void>((resolve) => {
      this.setState({
        powerConsumption,
        weatherObservations,
        isLoading: false,
      }, () => resolve())
    })
    await this.setGraphData()
  }

  setGraphData = async () => {
    try {
      const pinConfigurations = Array.from(this.state.pinConfigByRepresentation.values());
      
      const temperatureAndHumidityConfigs = pinConfigurations.filter((config: any) => 
        config.type === 'temperature' || config.type === 'humidity'
      );

      const sensor_threshold: any = {};
      
      temperatureAndHumidityConfigs.forEach((config: any) => {
        if (config.type === 'temperature') {
          const minF = config.representation.includes('room_temp') ? -130 : -50;
          const maxF = config.representation.includes('room_temp') ? 120 : 300;
          sensor_threshold[config.representation] = {
            min: minF,
            max: maxF,
            minC: temperatureService.convertAndFormat(minF, 'C', 2),
            maxC: temperatureService.convertAndFormat(maxF, 'C', 2)
          };
        } else if (config.type === 'humidity') {
          sensor_threshold[config.representation] = {
            min: 0,
            max: 100
          };
        }
      });

      const sensorsForConfig = this.getSelectedConfigSensorBoxSensors();
      const selectedIds = new Set(
        (this.state.selectedGrid?.flatMap((grid: any) => grid?.parameters || []) || [])
          .filter((id: string) => !!id)
      );
      const temperatureKeys = new Set(
        sensorsForConfig
          .filter((s: any) => s.pin_type === 'temperature' || s.type === 'temperature')
          .map((s: any) => s.pin_with_representation || `${s.representation}_${s.sensor_name}`)
      );

      let y_data: any = {}
      let x_time: any = {}
      let threshold:any = {}
      sensorsForConfig.forEach((sensor: any) => {
        const key = `${sensor.representation}_${sensor.sensor_name}`;
        if (!selectedIds.has(key)) {
          return;
        }
        y_data[key] = []
        x_time[key] = []
        if (sensor.min != null || sensor.max != null) {
          let minVal = sensor.min;
          let maxVal = sensor.max;
          threshold[key] = { min: minVal, max: maxVal };
        }
      });

      this.state.sensorData.forEach((item: any) => {
        if (this.state.isFilterredData) {
          Object.keys(y_data).forEach((representation) => {
            const value = item[representation];
          
            if (value !== null) {
              const matchingKey = Object.keys(sensor_threshold).find((key) =>
                representation.startsWith(key)
              );
              
              const thresholdLimit = matchingKey ? sensor_threshold[matchingKey] : null;
              let withinThreshold: boolean;
              if (thresholdLimit && temperatureKeys.has(representation) && this.state.temperatureUnit === 'C' && thresholdLimit.minC != null) {
                const valueC = temperatureService.convertAndFormat(value, 'C', 2);
                withinThreshold = valueC >= thresholdLimit.minC && valueC <= thresholdLimit.maxC;
              } else {
                withinThreshold = thresholdLimit ? value >= thresholdLimit.min && value <= thresholdLimit.max : true;
              }
          
              if (withinThreshold) {
                let plotValue = value;
                if (temperatureKeys.has(representation) && typeof value === 'number') {
                  plotValue = this.state.temperatureUnit === 'C'
                    ? temperatureService.convertAndFormat(value, 'C', 2)
                    : value;
                }
                y_data[representation].push(plotValue);
                x_time[representation].push(item.added_time);
              }
            }
          })
        } else {
          Object.keys(y_data).forEach((representation) => {
            if (item[representation] !== null) {
              let plotValue = item[representation];
              if (temperatureKeys.has(representation) && typeof plotValue === 'number') {
                plotValue = this.state.temperatureUnit === 'C'
                  ? temperatureService.convertAndFormat(plotValue, 'C', 2)
                  : plotValue;
              }
              y_data[representation].push(plotValue);
              x_time[representation].push(item.added_time);
            }
          })
        }
      })

      const wantsPower = selectedIds.has('powerConsumption')
      const wantsWeatherTemp = selectedIds.has('outsideWeatherTemperature')
      const wantsWeatherHumidity = selectedIds.has('outsideWeatherHumidity')

      if (wantsPower) {
        y_data['powerConsumption'] = []
        x_time['powerConsumption'] = []
        this.state.powerConsumption.forEach((item: any) => {
          y_data['powerConsumption'].push(item.total_energy_consuption)
          x_time['powerConsumption'].push(item.created_on)
        })
      }

      if (wantsWeatherTemp || wantsWeatherHumidity) {
        y_data['outsideWeatherTemperature'] = []
        x_time['outsideWeatherTemperature'] = []
        y_data['outsideWeatherHumidity'] = []
        x_time['outsideWeatherHumidity'] = []
      }

      if (wantsWeatherTemp || wantsWeatherHumidity) this.state.weatherObservations.forEach((item: any) => {
            if (item.time == null) {
              return
            }
            const tempF = parseFloat(item.temperature);
            if (!isNaN(tempF)) {
              const converted = this.state.temperatureUnit === 'C'
                ? temperatureService.convertAndFormat(tempF, 'C', 2)
                : tempF;
              y_data['outsideWeatherTemperature'].push(converted);
            } else {
              y_data['outsideWeatherTemperature'].push(item.temperature);
            }
            x_time['outsideWeatherTemperature'].push(item.time)
            y_data['outsideWeatherHumidity'].push(item.humidity != null ? item.humidity : null)
            x_time['outsideWeatherHumidity'].push(item.time)
      })
      Object.assign(this.state.y_data, y_data)
      this.setState({ y_data, x_time, threshold })
    } catch (error) {
      toast.error(error as any)
    }
  }

  closeModal = () => {
    this.props.closeGraphModal();
    this.setState({ 
      selectedSensor: '', 
      y_data: {}, 
      x_time: {}, 
      selectedParameters: [],
      selectedSensorsByType: {},
      weatherObservations: []
    })
  }

  handleGraphSelection = (e: any) => {
    this.setState({ selectedSensor: e })
  }

  attachPlotUnitsToConfigSensors = (sensors: any[] = []) => {
    const liveByKey = new Map<string, any>();
    this.props.sensorBox?.sensors?.forEach((s: any) => {
      liveByKey.set(`${s.representation}_${s.sensor_name}`, s);
    });
    const { pinConfigByRepresentation, temperatureUnit } = this.state;
    return sensors.map((sensor: any) => {
      const live = liveByKey.get(`${sensor.representation}_${sensor.sensor_name}`);
      const merged = {
        ...sensor,
        pin_type: live?.pin_type,
        unit: live?.unit,
        type: sensor.type || live?.pin_type,
      };
      return {
        ...merged,
        plot_unit: resolvePlotUnit(merged, pinConfigByRepresentation, temperatureUnit),
      };
    });
  };

  getSelectedConfigSensorBoxSensors = () => {
    let sensors = this.state.configMap?.get(parseInt(this.state.selectedConfig?._id))?.sensors
    sensors?.forEach((sensor: any) => sensor.pin_with_representation = `${sensor.representation}_${sensor.sensor_name}`);
    const hasCurrent = this.hasCurrentPinType();
    const { pinConfigByRepresentation, temperatureUnit } = this.state;
    const specialSensors = [
      ...(hasCurrent ? [{
        pin_with_representation: "powerConsumption",
        representation: "powerConsumption",
        type: "power",
        _id: "powerConsumption",
        display_name: "Power Consumption",
        plot_unit: resolvePlotUnit(
          { representation: 'powerConsumption', type: 'power' },
          pinConfigByRepresentation,
          temperatureUnit
        ),
      }] : []),
      {
        pin_with_representation:"outsideWeatherTemperature",
        representation:"outsideWeatherTemperature",
        type: "temperature",
        _id:  "outsideWeatherTemperature",
        display_name:"Outside Weather Temperature",
        plot_unit: resolvePlotUnit(
          { representation: 'outsideWeatherTemperature', type: 'temperature' },
          pinConfigByRepresentation,
          temperatureUnit
        ),
      },
      {
        pin_with_representation:"outsideWeatherHumidity",
        representation:"outsideWeatherHumidity",
        type: "humidity",
        _id:  "outsideWeatherHumidity",
        display_name:"Outside Weather Humidity",
        plot_unit: resolvePlotUnit(
          { representation: 'outsideWeatherHumidity', type: 'humidity' },
          pinConfigByRepresentation,
          temperatureUnit
        ),
      },
    ];
    sensors = this.attachPlotUnitsToConfigSensors(sensors || []);
    sensors=[...sensors, ...specialSensors]
    return sensors
  }

  // handle click event of the Remove button
  handleRemoveClick = (index: any) => {
    const list = [...this.state.selectedGrid];
    list.splice(index, 1);
    this.setState({ selectedGrid: list }, () => {
      setTimeout(() => {
        const plotElements = document.querySelectorAll('.js-plotly-plot');
        plotElements.forEach((plotElement: any) => {
          if (plotElement && plotElement.data) {
            Plotly.Plots.resize(plotElement);
          }
        });
      }, 0);
    });
  };

  // handle click event of the Add button
  handleAddClick = () => {
    this.setState({ selectedGrid: [...this.state.selectedGrid, { parameters: [] }] }, () => {
      setTimeout(() => {
        const plotElements = document.querySelectorAll('.js-plotly-plot');
        plotElements.forEach((plotElement: any) => {
          if (plotElement && plotElement.data) {
            Plotly.Plots.resize(plotElement);
          }
        });
      }, 0);
    });
  };

  handleConditionDropDownChange = (option: any, index: any) => {
    const optionValue = option.map((item: any) => item.value)
    
    const sensorsByType: any = {};
    let hasError = false;
    optionValue.forEach((sensorId: string) => {
      if (hasError) return;
      const sensor = this.state.sensorOptions.find((s: any) => s._id === sensorId);
      if (sensor) {
        const pinType = sensor.pin_type || 'default';
        if (!sensorsByType[pinType]) {
          const currentTypeCount = Object.keys(sensorsByType).length;
          if (currentTypeCount >= 2) {
            toast.error('Maximum 2 sensor types allowed');
            hasError = true;
            return;
          }
          sensorsByType[pinType] = [];
        }
        sensorsByType[pinType].push(sensorId);
      }
    });
    
    if (hasError) {
      return;
    }
    
    const maxPerType = Math.max(...Object.values(sensorsByType).map((arr: any) => arr.length));
    if (maxPerType > MAX_SENSORS_PER_TYPE) {
      toast.error(`Maximum ${MAX_SENSORS_PER_TYPE} sensors allowed per type`);
      return;
    }
    
    const list = [...this.state.selectedGrid];
    list[index]['parameters'] = optionValue;
    this.setState({ selectedGrid: list }, async () => {
      await this.syncPlotFromSelection()
    })
  };
  

  getParameterValues = (values: any) => {
    const parametersArray = this.state.sensorOptions?.filter((item: any) => (
      values?.includes(item?._id)
    ))
    const parameterValues = parametersArray?.map((item: any) => ({ value: item._id, label: item.name }))
    return parameterValues
  }
  
  onChangeStartDate = (value: any) => {
    const endDate = clampEndDateForStart(value, this.state.endDate)
    this.setState({ startDate: value, endDate, dateError: '' }, async () => {
      await this.loadHvacData()
    })
  }

  onChangeEndDate = async (value: any) => {
    const startDate = clampStartDateForEnd(this.state.startDate, value)
    this.setState({ startDate, endDate: value, dateError: '' }, async () => {
      await this.loadHvacData()
    })
  }

  render() {
    const graphClassName = ['multiple-graph w-100 pt-3 pb-3']
    if(this.state.selectedGrid.length > 1){
      graphClassName.push('col-6')
    }else{
      graphClassName.push('col-12')
    }

    return (
      <div className="modal fade bd-example-modal-sm show" id="graphmodal" role="dialog" aria-labelledby="exampleModalLabel">
        <div className="modal-dialog modal-sensor-data-graph" role="document">
          <div className="modal-content">
            <div className="modal-header ">
              <h5 className="modal-title" id="exampleModalLabel">{<div>{this.props.SensorBoxName} {this.props.sensorBox.alias && <span className='pl-2'>( {this.props.sensorBox.alias} )</span>}</div>}</h5>
              <span  className="close" onClick={() => { this.closeModal() }}>&times;</span>
            </div>
            <div className="row mt-50 mb-25 ml-2 ht-custom" style={{marginRight:0}}>

              <div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp">
                <div className="form-group mb-0">
                  <DatePicker
                    value={this.state.startDate}
                    maxDate={this.state.endDate}
                    onChange={(value: Date) => this.onChangeStartDate(value)}
                    placeholderText="Start Date"
                  />
                </div>
              </div>
              <div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp">
                <div className="form-group mb-0">
                  <DatePicker
                    value={this.state.endDate}
                    minDate={this.state.startDate}
                    maxDate={new Date()}
                    onChange={(value: Date) => this.onChangeEndDate(value)}
                    placeholderText="End Date"
                  />
                </div>
              </div>
              {this.state.configOptions.length > 0 &&
                <div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp">
                  <div className="form-group mb-0">
                    <Select name="type"
                      data={this.state.configOptions}
                      value={this.state.selectedConfig}
                      onChange={this.handleConfigSelection}
                    />
                  </div>
                </div>}

              {this.state.sensorOptions.length > 0 &&
                <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2 media-margin-tp">
                  <div className="form-group mb-0">
                    <button className="btn btn-block default-btn btn-custom add-new-graph-btn" style={{borderRadius:4}} onClick={this.handleAddClick}>Add New Graph</button>
                  </div>
                </div>
              }
            </div>

            <div className="row justify-between items-center space-x-6" style={{ marginLeft:15,marginRight:0}}>              
              <div className="error-msg text-red-600" style={{ marginLeft:15}}>{this.state.dateError}</div>
              { this.state.sensorOptions.length > 0  && 
                <div className={`switch-head `} style={{ marginRight:15, justifyContent:"end", display: 'flex', gap: '15px', alignItems: 'center' }}>
                  {this.state.sensorboxTimezone && (
                    <Checkbox
                      checked={this.state.useLocationTimezone}
                      onChange={(e) => {
                        this.setState({ useLocationTimezone: e.target.checked });
                      }}
                      className="custom-checkbox-teal"
                    >
                      <span className="font-w-600">Show Device Time</span>
                    </Checkbox>
                  )}
                  <Tooltip title={this.state.isFilterredData ? "Show Raw Data" : "Show Clean Data"} placement="left">
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span className="text-secondary small">Data: </span>
                      <div className={`toggle-switch ${this.state.isFilterredData ? "on" : "off"}`}
                        onClick={async (e) => {
                          await this.handleFilterDataToggle();
                        }}>
                        <div className="toggle-handle"></div>
                      </div>
                    </div>
                  </Tooltip>
                </div>
              }
            </div>

            <div className="modal-body modal-height-graph px-3">
              <div className="d-flex flex-wrap w-100 ml-0">
                {(this.state.sensorOptions.length > 0 )?
                  this.state.selectedGrid?.map((grid: any, index: any) => {
                    const paramValues = this.getParameterValues(grid?.parameters)
                    return (
                      <>
                        <div className={graphClassName.join(' ')}>
                          <div className='border p-3 h-100'>

                            <div className='d-flex mb-3'>
                              {(this.state.sensorOptions?.length !== 0)  &&
                                <div className="flex-fill media-margin-tp select-min">
                                  <div className="form-group mb-0">
                                    <Select
                                      isMulti={true}
                                      name="parameters"
                                      data={this.state.sensorOptions}
                                      value={paramValues}
                                      onChange={(e: any) => this.handleConditionDropDownChange(e, index)}
                                      maxOptions={12}
                                    />
                                  </div>
                                </div>
                              }

                              <div className='flex-auto ml-4'>
                                <div className="form-group">
                                  {
                                    this.state.selectedGrid.length !== 1 &&
                                    <Button className="mr-3" type="primary" shape="circle" onClick={() => this.handleRemoveClick(index)} ghost>
                                      <CloseOutlined />
                                    </Button>
                                  }
                                </div>
                              </div>
                            </div>

                            {(grid?.parameters?.length > 0) ?
                              <Graph
                                key={index}
                                fieldTestOfflineDuration = {this.state.fieldTestOfflineDuration}
                                category={this.props.sensorBox?.category}
                                index={index}
                                gridsCount={this.state.selectedGrid?.length}
                                x={this.state.x_time}
                                y={this.state.y_data}
                                threshold={this.state.threshold}
                                param={paramValues}
                                sensor_values={this.getSelectedConfigSensorBoxSensors()?? []}
                                configDate={this.state.configDateString}
                                sensorBox={this.props.sensorBox}
                                selectedSensorsByType={this.state.selectedSensorsByType}
                                sensorGroups={this.state.sensorGroups}
                                timezone={this.state.useLocationTimezone ? this.state.sensorboxTimezone : null}
                                temperatureUnit={this.state.temperatureUnit}
                              />
                              :
                              <div className="no-data-message w-100 col-12">Select parameters to view the graph</div>
                            }
                          </div>
                        </div>
                      </>
                    );
                  })
                  : <div className="no-data-message w-100 col-12">No Data Available</div>}
              </div>
            </div>
            <div className="modal-footer modal-footer-custom-padding">
            </div>
          </div>
        </div>
        {this.state.isLoading ?
          <LoadingWheel />
          : null}
      </div>
    );
  }
}

export default GraphModal;